<template>
  <div class="fullscreen-product-gallery__wrapper">
    <div class="fullscreen-product-gallery__container">
      <div class="fullscreen-product-gallery">
        <!-- media -->
        <swiper
          v-if="mounted"
          :instance-name="[`${liquid.sectionId}-fullscreen-images`]"
          :navigation="navigation"
          :modules="modules"
          :zoom="true"
          :initial-slide="currentIndex"
          :pagination="{
            el: '.fullscreen-product-gallery__pagination-wrapper',
            type: 'bullets',
          }"
          :thumbs="{
            swiper: fullscreenThumbsSwiper
          }"
          class="fullscreen-product-gallery__images-wrapper"
          @slide-change="getFullscreenCurrentIndex(); pauseAllVideos();"
          @swiper="setFullscreenImagesSwiper"
        >
          <template
            v-for="(media, index) in productMedia"
            :key="`image-${index}-${media.id}`"
          >
            <swiper-slide
              v-if="media.media_type == 'image'"
              class="fullscreen-product-gallery__image-container"
            >
              <div class="swiper-zoom-container">
                <responsive-image
                  v-if="media"
                  class="fullscreen-product-gallery__image swiper-zoom-target"
                  :alt="media.alt"
                  :image="media"
                  :lazy-load="true"
                  min-max="600-1200"
                  ratio="1:1"
                />
              </div>
            </swiper-slide>
            <swiper-slide
              v-if="media.media_type == 'video'"
              class="fullscreen-product-gallery__video-slide"
            >
              <div class="fullscreen-product-gallery__video-container">
                <video-element
                  :srcset="media.sources"
                  :controls="true"
                  :loop="false"
                  :autoplay="false"
                  :title="media.alt"
                  :poster="media.preview_image.src"
                  :muted="false"
                />
              </div>
            </swiper-slide>
          </template>
        </swiper>

        <button
          :class="[`swiper-button-prev-${liquid.sectionId}`]"
          class="collection-icons__swiper-arrow swiper-arrow swiper-button-prev"
        >
          <span
            class="visually-hidden"
            v-text="`${liquid.previousSlideLabel}`"
          />
          <icon-chevron-left />
        </button>

        <button
          :class="[`swiper-button-next-${liquid.sectionId}`]"
          class="collection-icons__swiper-arrow swiper-arrow swiper-button-next"
        >
          <span
            class="visually-hidden"
            v-text="`${liquid.nextSlideLabel}`"
          />
          <icon-chevron-right />
        </button>

        <div class="fullscreen-product-gallery__thumbnails-and-pagination-wrapper">
          <!-- thumbnails -->
          <swiper
            :instance-name="[`${liquid.sectionId}-fullscreen-thumbnails`]"
            :space-between="8"
            :slides-per-view="'auto'"
            :modules="modules"
            :initial-slide="currentIndex"
            slide-to-clicked-slide
            class="fullscreen-product-gallery__thumbnails-wrapper"
            @swiper="setFullscreenThumbsSwiper"
          >
            <template
              v-for="(media, index) in productMedia"
              :key="`thumbnail-${index}-${media.id}`"
            >
              <swiper-slide
                v-if="media.media_type == 'image'"
                class="fullscreen-product-gallery__thumbnail-container"
                :class="{ 'thumbnail-slide-active': index === currentIndex }"
              >
                <responsive-image
                  class="fullscreen-product-gallery__thumbnail"
                  :alt="media.alt"
                  :image="media"
                  :lazy-load="true"
                  ratio="1:1"
                />
              </swiper-slide>
              <swiper-slide
                v-if="media.media_type == 'video'"
                class="fullscreen-product-gallery__thumbnail-container"
                :class="{ 'thumbnail-slide-active': index === currentIndex }"
              >
                <responsive-image
                  class="fullscreen-product-gallery__thumbnail"
                  :alt="media.alt"
                  :image="media.preview_image"
                  :lazy-load="true"
                  ratio="1:1"
                />
                <icon-video-start />
              </swiper-slide>
            </template>
          </swiper>

          <div class="fullscreen-product-gallery__pagination-wrapper" />
        </div>
      </div>
    </div>
    <button
      class="fullscreen-product-gallery__close-button"
      @click="$emit('close')"
    >
      <span
        class="visually-hidden"
        v-text="`${liquid.closeFullscreenLabel}`"
      />
      <icon-close />
    </button>
  </div>
</template>

<script>

/**
 * Vue: Fullscreen product gallery (fullscreen-product-gallery)
 * -----------------------------------------------------------------------------
 * Fullscreen product images gallery.
 *
 */
import { ref, nextTick } from 'vue'

import { A11y, Pagination, Navigation, Thumbs, Zoom } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'

import VideoElement from '~async/video-element/video-element'
import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'
import IconClose from '~icons/misc/close.svg'
import IconVideoStart from '~icons/video/start.svg'

export default {
  name: 'FullscreenProductGallery',

  components: {
    Swiper,
    SwiperSlide,
    VideoElement,
    IconChevronLeft,
    IconChevronRight,
    IconClose,
    IconVideoStart,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
  },

  emits: ['close', 'updated-index'],

  setup(props) {

    const fullscreenImagesSwiper = ref(null)
    const fullscreenThumbsSwiper = ref(null)

    const setFullscreenImagesSwiper = (swiper) => {
      fullscreenImagesSwiper.value = swiper
    }

    const setFullscreenThumbsSwiper = (swiper) => {
      fullscreenThumbsSwiper.value = swiper
    }

    return {
      fullscreenImagesSwiper,
      fullscreenThumbsSwiper,
      setFullscreenImagesSwiper,
      setFullscreenThumbsSwiper,
      modules: [
        A11y,
        Navigation,
        Pagination,
        Zoom,
        Thumbs,
      ],
      navigation: {
        nextEl: `.swiper-button-next-${props.liquid.sectionId}`,
        prevEl: `.swiper-button-prev-${props.liquid.sectionId}`,
      },
    }
  },

  data() {
    return {
      mounted: false,
    }
  },

  computed: {

    /**
     * Compute the product images.
     * @returns {Object}
     */
    productMedia() {
      if (!this.product || !this.variant) {
        return
      }

      const media = [...this.liquid.media]

      const filteredImages = media.filter((media) => {
        return media.src?.includes(this.variant.sku)
      })

      return filteredImages
    },

  },

  mounted() {
    nextTick(() => {
      this.mounted = true
    })
  },

  methods: {

    /**
     * Get current index of Swiper slides.
     */
    getFullscreenCurrentIndex() {
      const index = this.fullscreenImagesSwiper?.activeIndex

      if (!Number.isInteger(index)) {
        return
      }

      this.setFullscreenCurrentIndex(index)
    },

    /**
     * Pause all videos in the document.
     */
    pauseAllVideos() {
      const video = document.querySelectorAll('video')

      if (!video) {
        return
      }

      video.forEach((element) => {
        element.pause()
      })
    },

    /**
     * Set current index of Swiper slides.
     */
    setFullscreenCurrentIndex(index) {
      this.$emit('updated-index', index)
    },

  },
}
</script>

<style lang="scss">
@import './fullscreen-product-gallery';
</style>